<script setup lang="ts">
  import { ref, computed, watch, onMounted } from 'vue';
  import { IconImage, ColoredButton } from '@/shared/ui';
  import { MAX_URLS_PER_UPLOAD, MIN_DOCUMENT_TITLE_LENGTH } from '@/shared/config';
  import { validateUrl, formatUrl } from '../../model';
  import type { UrlInput } from '@/features/Document/model/upload/types';
  import LinkIcon from '@/shared/assets/icons/LinkIcon.svg';
  import PlusIcon from '@/shared/assets/icons/PlusIcon.svg';
  import TrashIcon from '@/shared/assets/icons/TrashIcon.svg';

  interface Props {
    index?: number;
    instanceId?: number;
    isLast?: boolean;
    isFirst?: boolean;
    showRemoveButton?: boolean;
    duplicateTitlesFromBackend?: string[];
  }

  const props = withDefaults(defineProps<Props>(), {
    index: 0,
    instanceId: 0,
    isLast: true,
    isFirst: true,
    showRemoveButton: false,
    duplicateTitlesFromBackend: () => [],
  });

  const backendDuplicateSet = computed(
    () =>
      new Set(
        props.duplicateTitlesFromBackend?.map((t) => t.trim().toLowerCase()) ?? []
      )
  );

  const batchDuplicateNormalizedTitles = computed(() => {
    const titleCounts = urlInputs.value.reduce((acc, input) => {
      const name = input.documentName.trim().toLowerCase();
      if (name) {
        acc.set(name, (acc.get(name) ?? 0) + 1);
      }
      return acc;
    }, new Map<string, number>());
    const duplicates = new Set<string>();
    for (const [title, count] of titleCounts.entries()) {
      if (count > 1) {
        duplicates.add(title);
      }
    }
    return duplicates;
  });

  const duplicateSet = computed(() => {
    const set = new Set(backendDuplicateSet.value);
    batchDuplicateNormalizedTitles.value.forEach((t) => set.add(t));
    return set;
  });

  const emit = defineEmits<{
    (e: 'validationChange', payload: { areAllValid: boolean; hasValidUrls: boolean }): void;
    (e: 'valid', isValid: boolean): void;
    (e: 'remove', index: number): void;
    (e: 'clearDuplicateTitles'): void;
  }>();

  const urlInputs = ref<UrlInput[]>([
    { id: 0, url: '', documentName: '', isValid: false, urlError: '' },
  ]);
  const nextInputId = ref(1);
  const isSubmitting = ref(false);

  const addUrlInput = () => {
    urlInputs.value.push({
      id: nextInputId.value,
      url: '',
      documentName: '',
      isValid: false,
      urlError: '',
    });
    nextInputId.value++;
  };

  const removeUrlInput = (index: number) => {
    urlInputs.value.splice(index, 1);
    validateAllInputs();
  };

  const validateInput = (input: UrlInput): boolean => {
    const urlValidation = validateUrl(input.url);
    input.urlError = urlValidation.error;

    const hasValidName = !!(
      input.documentName.trim() && input.documentName.length >= MIN_DOCUMENT_TITLE_LENGTH
    );
    return urlValidation.isValid === true && hasValidName;
  };

  const validateAllInputs = () => {
    urlInputs.value.forEach((input) => {
      input.isValid = validateInput(input);
    });
  };

  const updateUrl = (index: number, value: string) => {
    urlInputs.value[index].url = value;
    urlInputs.value[index].isValid = validateInput(urlInputs.value[index]);
  };

  const updateDocumentName = (index: number, value: string) => {
    urlInputs.value[index].documentName = value;
    urlInputs.value[index].isValid = validateInput(urlInputs.value[index]);
    emit('clearDuplicateTitles');
  };

  const areAllUrlsValid = computed(() => {
    return (
      urlInputs.value.length > 0 &&
      urlInputs.value.every((input) => input.isValid) &&
      batchDuplicateNormalizedTitles.value.size === 0
    );
  });

  const hasValidUrls = computed(() => {
    return urlInputs.value.some((input) => input.isValid);
  });

  const resetUrlInputs = () => {
    urlInputs.value = [{ id: 0, url: '', documentName: '', isValid: false, urlError: '' }];
    nextInputId.value = 1;
  };

  const getFormattedInputs = () => {
    return urlInputs.value.map((item) => ({
      ...item,
      url: formatUrl(item.url),
    }));
  };

  watch([areAllUrlsValid, hasValidUrls], ([allValid, hasValid]) => {
    emit('validationChange', { areAllValid: allValid, hasValidUrls: hasValid });
    emit('valid', allValid);
  });

  onMounted(() => {
    emit('validationChange', {
      areAllValid: areAllUrlsValid.value,
      hasValidUrls: hasValidUrls.value,
    });
    emit('valid', areAllUrlsValid.value);
  });

  defineExpose({
    areAllUrlsValid,
    hasValidUrls,
    urlInputs,
    resetUrlInputs,
    getFormattedInputs,
    batchDuplicateNormalizedTitles,
  });
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      v-for="(input, index) in urlInputs"
      :key="input.id"
      class="flex flex-col gap-6 text-sm border border-dashed border-gray-02 rounded-lg px-6 py-8"
    >
      <div>
        <div class="flex justify-between">
          <h3 class="text-gray-07 font-semibold mb-2">
            Adicionar documento via URL:
          </h3>
          <IconImage
            v-if="urlInputs.length > 1 || (showRemoveButton && urlInputs.length === 1)"
            class="cursor-pointer text-gray-xx-02"
            :src="TrashIcon"
            alt="Remover"
            @click="removeUrlInput(index)"
          />
        </div>
        <div class="relative">
          <input
            :value="input.url"
            type="text"
            :class="[
              'px-4 py-3 pl-10 w-full text-gray-03 rounded-md focus:outline-none border',
              input.urlError ? 'border-red-16' : 'border-gray-02',
            ]"
            placeholder="Cole aqui a URL do documento"
            @input="updateUrl(index, ($event.target as HTMLInputElement).value)"
          >
          <IconImage
            :src="LinkIcon"
            alt="URL"
            class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
          />
        </div>
        <p
          v-if="input.urlError"
          class="text-red-16 text-sm mt-1"
        >
          {{ input.urlError }}
        </p>
      </div>

      <div>
        <h3 class="text-gray-07 font-medium mb-2">
          Nome do Documento:
        </h3>
        <input
          :value="input.documentName"
          type="text"
          :class="[
            'px-4 py-3 w-full text-gray-03 rounded-md focus:outline-none border',
            duplicateSet.has(input.documentName.trim().toLowerCase()) ? 'border-red-16' : 'border-gray-02',
          ]"
          placeholder="Escreva aqui o nome do documento"
          @input="updateDocumentName(index, ($event.target as HTMLInputElement).value)"
        >
      </div>

      <div
        v-if="index === urlInputs.length - 1 && isLast"
        class="flex justify-end"
      >
        <ColoredButton
          :icon="PlusIcon"
          text="Adicionar URL"
          :loading="isSubmitting"
          :disabled="urlInputs.length >= MAX_URLS_PER_UPLOAD"
          @click="addUrlInput"
        />
      </div>
    </div>
  </div>
</template>

<style>
  #inputs-wrapper select,
  #inputs-wrapper input {
    height: 44px;
  }
</style>
